import { CustomSelectFieldProps, SelectField } from 'components/form/core'
import { resolveName } from 'components/form/core/hooks/useField'
import { ResponsavelFamiliarSelectFieldDocument } from 'graphql/hooks.generated'
import {
  ResponsavelFamiliarSelectFieldQuery,
  ResponsavelFamiliarSelectFieldQueryVariables,
} from 'graphql/types.generated'
import { useField } from 'react-final-form'
import { applyMask, masks } from 'util/mask'
import { isCnsLike, isValidCns } from 'util/validation/rules/cns'
import { isCpfLike, isValidCpf } from 'util/validation/rules/cpf'

import { DEFAULT_SELECT_PAGE_PARAM, useAsyncQuerySelect } from '../useAsyncQuerySelect'
import { ResponsavelFamiliarSelectItem } from './ResponsavelFamiliarSelectItem'

const DEBOUNCE_TIME = 500
const MIN_INPUT_LENGTH = 3

export type ResponsavelFamiliarSelectFieldModel = {
  id?: string
  nome?: string
  nomeSocial?: string
  cpf?: string
  cns?: string
  uuidUltimaFicha?: string
  statusResponsavelFamiliar?: boolean
}

export type ResponsavelFamiliarSelectFieldProps = CustomSelectFieldProps<ResponsavelFamiliarSelectFieldModel>

export function ResponsavelFamiliarSelectField(props: ResponsavelFamiliarSelectFieldProps) {
  const { input } = useField<ResponsavelFamiliarSelectFieldModel>(resolveName(props.name), {
    subscription: { value: true },
  })

  const {
    selectProps: { ...asyncProps },
  } = useAsyncQuerySelect<
    ResponsavelFamiliarSelectFieldModel,
    ResponsavelFamiliarSelectFieldQuery,
    ResponsavelFamiliarSelectFieldQueryVariables
  >({
    query: ResponsavelFamiliarSelectFieldDocument,
    extractItems: (data) => {
      const items = data?.cidadaosCombo?.filter((cidadao) => cidadao?.cpf ?? cidadao?.cns) ?? []
      const currentValue = input.value
      const cpfCnsInput = currentValue?.cpf?.onlyDigits() ?? currentValue?.cns?.onlyDigits()

      if (cpfCnsInput && !currentValue?.id) {
        const itemFound = items.find(
          (item) => item?.cpf?.onlyDigits() === cpfCnsInput || item?.cns?.onlyDigits() === cpfCnsInput
        )
        if (itemFound) {
          input.onChange(itemFound)
        }
      }

      return items
    },
    variables: (inputString) => ({
      input: {
        query: inputString,
        ativo: true,
        obito: false,
        limit: DEFAULT_SELECT_PAGE_PARAM.size,
      },
      emAtencaoDomiciliar: false,
    }),
    skip: (inputString) => inputString?.trim()?.length < MIN_INPUT_LENGTH,
    debounceTime: DEBOUNCE_TIME,
  })

  const createNewItem = (text: string): ResponsavelFamiliarSelectFieldModel | null => {
    const cleanedText = text?.onlyDigits()

    if (isCnsLike(cleanedText) && isValidCns(cleanedText)) {
      return { cns: cleanedText }
    }

    if (isCpfLike(cleanedText) && isValidCpf(cleanedText)) {
      return { cpf: cleanedText }
    }

    return null
  }

  const itemToString = (item: ResponsavelFamiliarSelectFieldModel | null): string => {
    if (!item) return ''

    if (item.id) {
      const nome = item.nomeSocial ?? item.nome ?? ''
      const cpfOrCns = item.cpf ? applyMask(item.cpf, masks.cpf) : item.cns ?? ''
      return `${cpfOrCns} (${nome})`
    }

    if (item.cpf) {
      return `${applyMask(item.cpf, masks.cpf)} (Cidadão não cadastrado na base local)`
    }
    if (item.cns) {
      return `${item.cns} (Cidadão não cadastrado na base local)`
    }

    return ''
  }

  const renderItem = (item: ResponsavelFamiliarSelectFieldModel) => {
    return <ResponsavelFamiliarSelectItem item={item} />
  }

  return (
    <SelectField<ResponsavelFamiliarSelectFieldModel>
      itemToString={itemToString}
      renderItem={renderItem}
      createNewItem={createNewItem}
      placeholder='Pesquise pelo nome, CPF ou CNS.'
      icon={null}
      {...asyncProps}
      {...props}
    />
  )
}
